// Reports API functions
import { config } from '../config/environment';
import { logger } from './logger';

// Report Service API base URL
const REPORT_API_BASE = config.reportApiBase || '';

/**
 * Get Azure AD access token for Report API authentication
 * Uses the existing MSAL instance with report-specific scopes
 * @returns {Promise<string>} The access token
 */
const getAccessToken = async () => {
  // First try to use the global token getter set up by useAuthenticatedApi hook
  if (typeof window !== 'undefined' && window.getAccessToken) {
    try {
      // Determine the correct scope based on environment
      const scope = config.isProduction 
        ? `api://${process.env.REACT_APP_AZURE_AD_CLIENT_ID}/report-api-prod1.access_as_user`
        : `api://${process.env.REACT_APP_AZURE_AD_CLIENT_ID}/report-api-dev.access_as_user`;
      
      return await window.getAccessToken(scope);
    } catch (error) {
      logger.warn('Global getAccessToken failed, falling back to direct MSAL:', error);
    }
  }
  
  // Fallback to direct MSAL access for non-React contexts
  const { msalInstance } = await import('../config/msalConfig');
  
  // Determine the correct scope based on environment
  const environment = process.env.NODE_ENV || 'development';
  const scope = environment === 'production' 
    ? `api://${process.env.REACT_APP_AZURE_AD_CLIENT_ID}/report-api-prod1.access_as_user`
    : `api://${process.env.REACT_APP_AZURE_AD_CLIENT_ID}/report-api-dev.access_as_user`;
  
  const accounts = msalInstance.getAllAccounts();
  
  if (accounts.length === 0) {
    throw new Error('No authenticated user found. Please log in first.');
  }
  
  const account = accounts[0];
  
  try {
    // Try to get token silently first with report-specific scope
    const response = await msalInstance.acquireTokenSilent({
      scopes: [scope],
      account: account,
    });
    return response.accessToken;
  } catch (silentError) {
    logger.warn('Silent token acquisition failed, trying interactive:', silentError);
    
    // Fall back to interactive method
    const response = await msalInstance.acquireTokenPopup({
      scopes: [scope],
    });
    return response.accessToken;
  }
};

/**
 * Make an authenticated request to the Report API
 * @param {string} endpoint - API endpoint path
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Response data
 */
const makeAuthenticatedRequest = async (endpoint, options = {}) => {
  const url = `${REPORT_API_BASE}${endpoint}`;
  
  try {
    const accessToken = await getAccessToken();
    
    const requestOptions = {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        ...options.headers,
      },
      ...options,
    };

    logger.log(`Making authenticated request to: ${url}`);
    const response = await fetch(url, requestOptions);
    
    if (!response.ok) {
      const errorText = await response.text();
      logger.error(`Report API request failed: ${response.status} ${response.statusText}`, {
        url,
        status: response.status,
        statusText: response.statusText,
        errorText,
      });
      throw new Error(`Report API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    logger.log('Report API request successful');
    return data;
  } catch (error) {
    logger.error('Error making authenticated Report API request:', error);
    throw error;
  }
};

/**
 * Get the full report for a canvass
 * @param {string} canvassId - The canvass ID
 * @param {Object} options - Query options
 * @returns {Promise<Object>} The report data
 */
export const getCanvassReport = async (canvassId, options = {}) => {
  if (!canvassId) {
    throw new Error('Canvass ID is required');
  }

  const queryParams = new URLSearchParams();
  if (options.returnFull) {
    queryParams.append('returnFull', 'true');
  }
  if (canvassId) {
    queryParams.append('canvassId', canvassId);
  }
  
  const endpoint = `/analytics/report${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  
  try {
    const response = await makeAuthenticatedRequest(endpoint, {
      method: 'GET',
    });
    
    return response;
  } catch (error) {
    logger.error(`Failed to get report for canvass ${canvassId}:`, error);
    throw error;
  }
};

/**
 * Post the admin executive summary for a canvass
 * @param {string} canvassId - The canvass ID
 * @param {string} adminExecutiveSummary - The admin executive summary text
 * @returns {Promise<Object>} The response data
 */
export const postAdminSummary = async (canvassId, adminExecutiveSummary) => {
  if (!canvassId) {
    throw new Error('Canvass ID is required');
  }

  if (!adminExecutiveSummary) {
    throw new Error('Admin executive summary is required');
  }

  const endpoint = `/report/admin/summary/${canvassId}`;

  try {
    const response = await makeAuthenticatedRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify({ adminExecutiveSummary })
    });

    return response;
  } catch (error) {
    logger.error(`Failed to post admin executive summary for canvass ${canvassId}:`, error);
    throw error;
  }
};

/**
 * Generate executive summary for a canvass
 * @param {string} canvassId - The canvass ID
 * @returns {Promise<Object>} The response data with the generated executive summary
 */
export const generateExecutiveSummary = async (canvassId) => {
  if (!canvassId) {
    throw new Error('Canvass ID is required');
  }

  const endpoint = `/analytics/report/generate-executive-summary/${canvassId}`;

  try {
    const response = await makeAuthenticatedRequest(endpoint, {
      method: 'POST',
    });

    return response;
  } catch (error) {
    logger.error(`Failed to generate executive summary for canvass ${canvassId}:`, error);
    // throw error;
  }
};

/**
 * Download report as JSON
 * @param {string} canvassId - The canvass ID
 * @param {Object} reportData - The report data to download
 * @param {string} canvassName - The canvass name for filename
 */
export const downloadReportJson = (canvassId, reportData, canvassName = 'Canvass') => {
  if (!reportData) {
    logger.warn('No report data to download');
    return;
  }

  try {
    const dataStr = JSON.stringify(reportData, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const safeName = String(canvassName).replace(/[^a-z0-9_-]+/gi, '_');
    const filename = `${safeName}_Report_${canvassId || 'unknown'}.json`;
    
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    logger.log(`Report JSON downloaded: ${filename}`);
  } catch (error) {
    logger.error('Failed to download report JSON:', error);
    throw error;
  }
};

/**
 * Export comprehensive report as HTML
 * @param {Object} reportData - The report data
 * @param {string} canvassName - The canvass name for filename
 * @returns {string} The HTML content
 */
export const exportComprehensiveReportHtml = (reportData, canvassName = 'Canvass') => {
  if (!reportData?.comprehensiveReport || typeof window === 'undefined') return;
  const nameBase = canvassName || 'Canvass';
  const safeName = String(nameBase).replace(/[^a-z0-9_-]+/gi, '_');
  const data = reportData.comprehensiveReport || {};
  const meta = data.reportMetadata || data.reportMeta || {};
  const performance = reportData.report?.performance || {};
  const header = data.header || {};
  const isV2 = data.schemaVersion === 'comprehensive_report.v2';
  const STATUS_LABELS = {
    not_started: 'Not started',
    sources_frozen: 'Sources frozen',
    hitl_analysis: 'In analysis',
    qa_review: 'QA review',
    approved: 'Approved',
    shared: 'Shared',
    feedback_captured: 'Feedback captured',
    learning_routed: 'Learning routed',
  };
  const OBJECTIVE_STATUS_LABELS = {
    answered: 'Answered',
    partially_answered: 'Partially answered',
    insufficient_evidence: 'Insufficient evidence',
    not_explored: 'Not explored',
  };
  const escapeHtml = (value) => {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };
  const formatValue = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (Array.isArray(value)) {
      return value
        .map((v) => {
          if (v === null || v === undefined) return '';
          if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
          if (typeof v === 'object') {
            if (typeof v.stage === 'string') return v.stage;
            if (typeof v.theme === 'string') return v.theme;
            if (typeof v.area === 'string') return v.area;
            if (typeof v.label === 'string') return v.label;
            if (typeof v.name === 'string') return v.name;
            if (typeof v.summary === 'string') return v.summary;
            if (typeof v.description === 'string') return v.description;
            if (typeof v.pattern === 'string') return v.pattern;
            if (typeof v.driver === 'string') return v.driver;
            if (typeof v.opportunity === 'string') return v.opportunity;
            if (typeof v.risk === 'string') return v.risk;
            if (typeof v.play === 'string') return v.play;
            if (typeof v.benefit === 'string') return v.benefit;
            if (typeof v.arc === 'string') return v.arc;
            return JSON.stringify(v);
          }
          return String(v);
        })
        .filter(Boolean)
        .join(', ');
    }
    if (typeof value === 'object') {
      if (typeof value.stage === 'string') return value.stage;
      if (typeof value.theme === 'string') return value.theme;
      if (typeof value.area === 'string') return value.area;
      if (typeof value.summary === 'string') return value.summary;
      if (typeof value.description === 'string') return value.description;
      if (typeof value.pattern === 'string') return value.pattern;
      if (typeof value.driver === 'string') return value.driver;
      if (typeof value.opportunity === 'string') return value.opportunity;
      if (typeof value.risk === 'string') return value.risk;
      if (typeof value.play === 'string') return value.play;
      if (typeof value.benefit === 'string') return value.benefit;
      if (typeof value.arc === 'string') return value.arc;
      return JSON.stringify(value);
    }
    return String(value);
  };
  const section = [];
  
  // Executive Summary
  const adminExecutiveSummary = reportData.adminExecutiveSummary || reportData.report?.adminExecutiveSummary || reportData?.comprehensiveReport?.adminExecutiveSummary;
  const objectiveAnswer = data.objectiveAnswer || reportData.objectiveAnswer;
  if (adminExecutiveSummary) {
    // Parse markdown formatting like the component does
    const parseMarkdownFormatting = (text) => {
      // Handle bold text (**text**)
      let processedText = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      
      // Handle italic text (*text*)
      processedText = processedText.replace(/\*(.*?)\*/g, '<em>$1</em>');
      
      return processedText;
    };
    
    const formattedSummary = adminExecutiveSummary
      .split('\n\n')
      .map((paragraph) => 
        paragraph.split('\n').map((line, lineIndex) => {
          const processedLine = parseMarkdownFormatting(line);
          return lineIndex > 0 ? `<br>${processedLine}` : processedLine;
        }).join('')
      )
      .join('</p><p style="margin-bottom: 1em; line-height: 1.5;">');
    
    section.push(`
      <section class="section-block">
        <h2 class="section-title">${isV2 ? 'Bottom Line' : 'Executive Summary'}</h2>
        <div class="section-body">
          <div class="lede">
            <p style="margin-bottom: 1em; line-height: 1.5;">${formattedSummary}</p>
          </div>
        </div>
      </section>
    `);
  }

  // Objective Answer
  if (!isV2 && objectiveAnswer && (objectiveAnswer.conclusion || Array.isArray(objectiveAnswer.prioritizedPolicyAreas) || Array.isArray(objectiveAnswer.secondaryAreas) || objectiveAnswer.evidenceCaveat)) {
    section.push(`
      <section class="section-block">
        <h2 class="section-title">Objective Answer</h2>
        <div class="section-body">
          ${objectiveAnswer.conclusion ? `<div class="lede"><p>${escapeHtml(objectiveAnswer.conclusion)}</p></div>` : ''}
          ${Array.isArray(objectiveAnswer.prioritizedPolicyAreas) && objectiveAnswer.prioritizedPolicyAreas.length > 0
            ? `<div class="subsection">
                <h3 class="subsection-title">Prioritized Policy Areas</h3>
                <div class="card-grid">
                  ${objectiveAnswer.prioritizedPolicyAreas.map((area) => `
                    <article class="card">
                      <h4 class="card-title">
                        ${escapeHtml(area?.policyArea || '---')}
                        ${typeof area?.rank === 'number' ? `<span class="pill">Rank ${area.rank}</span>` : ''}
                      </h4>
                      ${area?.problemStatement ? `<div class="field"><div class="field-label muted">Problem</div><div>${escapeHtml(area.problemStatement)}</div></div>` : ''}
                      ${area?.evidence ? `<div class="field"><div class="field-label muted">Evidence</div><div>${escapeHtml(area.evidence)}</div></div>` : ''}
                      ${area?.recommendedChange ? `<div class="field"><div class="field-label muted">Recommended Change</div><div>${escapeHtml(area.recommendedChange)}</div></div>` : ''}
                      ${area?.confidence ? `<div class="pill-row"><span class="pill">${escapeHtml(String(area.confidence))}</span></div>` : ''}
                      ${area?.caveat ? `<div class="field"><div class="field-label muted">Caveat</div><div>${escapeHtml(area.caveat)}</div></div>` : ''}
                    </article>
                  `).join('')}
                </div>
              </div>`
            : ''}
          ${Array.isArray(objectiveAnswer.secondaryAreas) && objectiveAnswer.secondaryAreas.length > 0
            ? `<div class="subsection">
                <h3 class="subsection-title">Secondary Areas</h3>
                <div class="card-grid">
                  ${objectiveAnswer.secondaryAreas.map((area) => `
                    <article class="card">
                      <h4 class="card-title">${escapeHtml(area?.topic || '---')}</h4>
                      ${area?.note ? `<p class="muted">${escapeHtml(area.note)}</p>` : ''}
                    </article>
                  `).join('')}
                </div>
              </div>`
            : ''}
          ${objectiveAnswer.evidenceCaveat ? `<div class="field"><div class="field-label muted">Evidence Caveat</div><div>${escapeHtml(objectiveAnswer.evidenceCaveat)}</div></div>` : ''}
        </div>
      </section>
    `);
  }
  
  // Analyzed Interviews
  if (meta.dataSummary || performance) {
    const stats = [];
    
    // Interview Processing Stats from dataSummary
    if (meta.dataSummary?.interviewsProcessed !== undefined) {
      stats.push(`
        <div class="field">
          <div class="field-label muted">Interviews Processed</div>
          <div>${escapeHtml(meta.dataSummary.interviewsProcessed.toLocaleString())}</div>
        </div>
      `);
    }
    
    // Performance Stats
    if (performance?.completedVoices !== undefined) {
      stats.push(`
        <div class="field">
          <div class="field-label muted">Completed Voices</div>
          <div>${escapeHtml(performance.completedVoices.toLocaleString())}</div>
        </div>
      `);
    }
    
    if (performance?.abandonedVoices !== undefined) {
      stats.push(`
        <div class="field">
          <div class="field-label muted">Abandoned Interviews</div>
          <div>${escapeHtml(performance.abandonedVoices.toLocaleString())}</div>
        </div>
      `);
    }
    
    // Completion Rate - prefer performance data, fallback to dataSummary
    if (performance?.completionRate !== undefined || meta.dataSummary?.completionRate !== undefined) {
      const completionRate = performance?.completionRate ?? meta.dataSummary?.completionRate ?? 0;
      stats.push(`
        <div class="field">
          <div class="field-label muted">Completion Rate</div>
          <div>${escapeHtml(completionRate.toFixed(1))}%</div>
        </div>
      `);
    }
    
    if (stats.length > 0) {
      section.push(`
        <section class="section-block">
          <h2 class="section-title">Analyzed Interviews</h2>
          <div class="section-body">
            <div class="card-grid">
              ${stats.join('')}
            </div>
          </div>
        </section>
      `);
    }
  }
  
  // Emotional Narratives
  if (!isV2 && data.emotionalArc) {
    const arc = data.emotionalArc;
    section.push(`
      <section class="section-block">
        <h2 class="section-title">Emotional Narratives</h2>
        <div class="section-body">
          ${arc.dominantPattern ? `<p class="lede">${escapeHtml(arc.dominantPattern)}</p>` : ''}
          ${arc.arcDescription ? `<p>${escapeHtml(arc.arcDescription)}</p>` : ''}
          ${Array.isArray(arc.emotionalBeats) && arc.emotionalBeats.length > 0
            ? `<div class="subsection">
                <h3 class="subsection-title">Key Emotional Moments</h3>
                <div class="card-grid">
                  ${arc.emotionalBeats.map((beat) => `
                    <article class="card">
                      <h4 class="card-title">${escapeHtml(beat?.stage || beat?.summary || '---')}</h4>
                      ${beat?.description ? `<p class="muted">${escapeHtml(beat.description)}</p>` : ''}
                      ${Array.isArray(beat?.primaryEmotions) && beat.primaryEmotions.length > 0
                        ? `<div class="pill-row">${beat.primaryEmotions
                            .map((e) => `<span class="pill">${escapeHtml(e)}</span>`)
                            .join('')}</div>`
                        : ''}
                    </article>
                  `).join('')}
                </div>
              </div>`
            : ''}
          ${Array.isArray(arc.emotionalRisks) && arc.emotionalRisks.length > 0
            ? `<div class="subsection">
                <h3 class="subsection-title">Emotional Risks</h3>
                <div class="pill-row">${arc.emotionalRisks
                  .map((r) => `<span class="pill">${escapeHtml(r)}</span>`)
                  .join('')}</div>
              </div>`
            : ''}
          ${Array.isArray(arc.emotionalOpportunities) && arc.emotionalOpportunities.length > 0
            ? `<div class="subsection">
                <h3 class="subsection-title">Emotional Opportunities</h3>
                <div class="pill-row">${arc.emotionalOpportunities
                  .map((o) => `<span class="pill">${escapeHtml(o)}</span>`)
                  .join('')}</div>
              </div>`
            : ''}
        </div>
      </section>
    `);
  }
  // Thematic Areas
  if (!isV2 && data.thematicAreas && typeof data.thematicAreas === 'object') {
    const areas = data.thematicAreas;
    section.push(`
      <section class="section-block">
        <h2 class="section-title">Thematic Areas</h2>
        <div class="section-body">
          <div class="card-grid">
            ${Object.entries(areas).map(([title, area]) => `
              <article class="card">
                <h3 class="card-title">${escapeHtml(String(title).replace(/[_-]+/g, ' '))}</h3>
                ${area?.summary
                  ? `<div class="field">
                      <div class="field-label muted">Summary</div>
                      <div>${escapeHtml(area.summary)}</div>
                    </div>`
                  : ''}
                ${area?.emotionalRole
                  ? `<div class="field">
                      <div class="field-label muted">Emotional Role</div>
                      <div>${escapeHtml(area.emotionalRole)}</div>
                    </div>`
                  : ''}
                ${Array.isArray(area?.signalsAndInferences) && area.signalsAndInferences.length > 0
                  ? `<div class="field">
                      <div class="field-label muted">Signals &amp; Inferences</div>
                      <div class="pill-row">${area.signalsAndInferences
                        .map((s) => `<span class="pill">${escapeHtml(s)}</span>`)
                        .join('')}</div>
                    </div>`
                  : ''}
                ${Array.isArray(area?.subThemes) && area.subThemes.length > 0
                  ? `<div class="field">
                      <div class="field-label muted">Sub-themes</div>
                      <div class="card-grid">
                        ${area.subThemes.map((t) => `
                          <article class="card">
                            <h4 class="card-title">${escapeHtml(t?.name || '---')}</h4>
                            ${t?.description ? `<p class="muted">${escapeHtml(t.description)}</p>` : ''}
                            ${Array.isArray(t?.evidence) && t.evidence.length > 0
                              ? `<div class="field">
                                  <div class="field-label muted">Evidence</div>
                                  <div class="pill-row">${t.evidence
                                    .map((ev) => `<span class="quote">${escapeHtml(ev)}</span>`)
                                    .join('')}</div>
                                </div>`
                              : ''}
                          </article>
                        `).join('')}
                      </div>
                    </div>`
                  : ''}
              </article>
            `).join('')}
          </div>
        </div>
      </section>
    `);
  }
  // Business Intelligence
  if (!isV2 && data.businessIntelligence && typeof data.businessIntelligence === 'object') {
    const bi = data.businessIntelligence;
    section.push(`
      <section class="section-block">
        <h2 class="section-title">Business Intelligence</h2>
        <div class="section-body">
          ${Array.isArray(bi.coreEmotionalDrivers) && bi.coreEmotionalDrivers.length > 0
            ? (() => {
                const isStringArray = typeof bi.coreEmotionalDrivers[0] === 'string';
                return `<div class="subsection">
                  <h3 class="subsection-title">Core Emotional Drivers</h3>
                  <div class="card-grid">
                    ${bi.coreEmotionalDrivers.map((driver) => isStringArray
                      ? `<article class="card">
                          <h4 class="card-title">${escapeHtml(formatValue(driver))}</h4>
                        </article>`
                      : `<article class="card">
                          <h4 class="card-title">${escapeHtml(driver?.theme || driver?.summary || '---')}</h4>
                          ${driver?.description ? `<p class="muted">${escapeHtml(formatValue(driver.description))}</p>` : ''}
                          ${driver?.pattern ? `<p class="muted">${escapeHtml(formatValue(driver.pattern))}</p>` : ''}
                          ${Array.isArray(driver?.needs) && driver.needs.length > 0
                            ? `<div class="field">
                                <div class="field-label muted">Needs</div>
                                <div class="pill-row">${driver.needs
                                  .map((n) => `<span class="pill">${escapeHtml(formatValue(n))}</span>`)
                                  .join('')}</div>
                              </div>`
                            : ''}
                        </article>`
                    ).join('')}
                  </div>
                </div>`;
              })()
            : ''}
          ${Array.isArray(bi.operationalRisks) && bi.operationalRisks.length > 0
            ? (() => {
                const isStringArray = typeof bi.operationalRisks[0] === 'string';
                return `<div class="subsection">
                  <h3 class="subsection-title">Operational Risks</h3>
                  <div class="card-grid">
                    ${bi.operationalRisks.map((risk) => isStringArray
                      ? `<article class="card">
                          <h4 class="card-title">${escapeHtml(formatValue(risk))}</h4>
                        </article>`
                      : `<article class="card">
                          <h4 class="card-title">${escapeHtml(risk?.area || risk?.summary || '---')}</h4>
                          ${risk?.description ? `<p class="muted">${escapeHtml(formatValue(risk.description))}</p>` : ''}
                          ${risk?.risk ? `<p class="muted">${escapeHtml(formatValue(risk.risk))}</p>` : ''}
                          ${Array.isArray(risk?.drivers) && risk.drivers.length > 0
                            ? `<div class="field">
                                <div class="field-label muted">Drivers</div>
                                <div class="pill-row">${risk.drivers
                                  .map((d) => `<span class="pill">${escapeHtml(formatValue(d))}</span>`)
                                  .join('')}</div>
                              </div>`
                            : ''}
                          ${risk?.emotionalImpact ? `<div class="field">
                              <div class="field-label muted">Emotional Impact</div>
                              <div class="muted">${escapeHtml(formatValue(risk.emotionalImpact))}</div>
                            </div>`
                          : ''}
                        </article>`
                    ).join('')}
                  </div>
                </div>`;
              })()
            : ''}
          ${Array.isArray(bi.opportunities) && bi.opportunities.length > 0
            ? (() => {
                const isStringArray = typeof bi.opportunities[0] === 'string';
                return `<div class="subsection">
                  <h3 class="subsection-title">Opportunities</h3>
                  <div class="card-grid">
                    ${bi.opportunities.map((opp) => isStringArray
                      ? `<article class="card">
                          <h4 class="card-title">${escapeHtml(formatValue(opp))}</h4>
                        </article>`
                      : `<article class="card">
                          <h4 class="card-title">${escapeHtml(opp?.area || opp?.summary || '---')}</h4>
                          ${opp?.description ? `<p class="muted">${escapeHtml(formatValue(opp.description))}</p>` : ''}
                          ${opp?.play ? `<p class="muted">${escapeHtml(formatValue(opp.play))}</p>` : ''}
                          ${Array.isArray(opp?.levers) && opp.levers.length > 0
                            ? `<div class="field">
                                <div class="field-label muted">Levers</div>
                                <div class="pill-row">${opp.levers
                                  .map((l) => `<span class="pill">${escapeHtml(formatValue(l))}</span>`)
                                  .join('')}</div>
                              </div>`
                            : ''}
                          ${opp?.benefit ? `<div class="field">
                              <div class="field-label muted">Benefit</div>
                              <div class="muted">${escapeHtml(formatValue(opp.benefit))}</div>
                            </div>`
                          : ''}
                        </article>`
                    ).join('')}
                  </div>
                </div>`;
              })()
            : ''}
          ${bi.satisfactionVolatility && typeof bi.satisfactionVolatility === 'object'
            ? `<div class="subsection">
                <h3 class="subsection-title">Satisfaction Volatility</h3>
                <div class="card-grid">
                  ${bi.satisfactionVolatility.threshold
                    ? `<article class="card">
                        <h4 class="card-title">Threshold</h4>
                        <p class="muted">${escapeHtml(formatValue(bi.satisfactionVolatility.threshold))}</p>
                      </article>`
                    : ''}
                  ${Array.isArray(bi.satisfactionVolatility.recoveryRequirements) && bi.satisfactionVolatility.recoveryRequirements.length > 0
                    ? `<article class="card">
                        <h4 class="card-title">Recovery Requirements</h4>
                        <div class="pill-row">${bi.satisfactionVolatility.recoveryRequirements
                          .map((req) => `<span class="pill">${escapeHtml(formatValue(req))}</span>`)
                          .join('')}</div>
                      </article>`
                    : ''}
                </div>
              </div>`
            : ''}
          ${Array.isArray(bi.satisfactionAndCoveragePatterns) && bi.satisfactionAndCoveragePatterns.length > 0
            ? `<div class="subsection">
                <h3 class="subsection-title">Satisfaction & Coverage Patterns</h3>
                <div class="card-grid">
                  ${bi.satisfactionAndCoveragePatterns.map((pattern) => `
                    <article class="card">
                      <h4 class="card-title">${escapeHtml(pattern?.segmentLabel || pattern?.pattern || '---')}</h4>
                      ${pattern?.emotionalArc ? `<p class="muted"><strong>Emotional Arc:</strong> ${escapeHtml(formatValue(pattern.emotionalArc))}</p>` : ''}
                      ${pattern?.description ? `<p class="muted">${escapeHtml(formatValue(pattern.description))}</p>` : ''}
                      ${Array.isArray(pattern?.traits) && pattern.traits.length > 0
                        ? `<div class="field">
                            <div class="field-label muted">Traits</div>
                            <div class="pill-row">${pattern.traits
                              .map((trait) => `<span class="pill">${escapeHtml(formatValue(trait))}</span>`)
                              .join('')}</div>
                          </div>`
                        : ''}
                      ${Array.isArray(pattern?.coverageStrength) && pattern.coverageStrength.length > 0
                        ? `<div class="field">
                            <div class="field-label muted">Coverage Strength</div>
                            <div class="pill-row">${pattern.coverageStrength
                              .map((strength) => `<span class="pill">${escapeHtml(formatValue(strength))}</span>`)
                              .join('')}</div>
                          </div>`
                        : ''}
                      ${pattern?.patternId ? `<div class="field">
                          <div class="field-label muted">Pattern ID</div>
                          <div class="muted">${escapeHtml(formatValue(pattern.patternId))}</div>
                        </div>`
                      : ''}
                    </article>
                  `).join('')}
                </div>
              </div>`
            : ''}
          ${bi.satisfactionAndCoverage && typeof bi.satisfactionAndCoverage === 'object'
            ? `<div class="subsection">
                <h3 class="subsection-title">Satisfaction & Coverage</h3>
                ${Array.isArray(bi.satisfactionAndCoverage?.emotionalArcs) && bi.satisfactionAndCoverage.emotionalArcs.length > 0
                  ? `<div class="card-grid">
                      ${bi.satisfactionAndCoverage.emotionalArcs.map((arc) => `
                        <article class="card">
                          <h4 class="card-title">${escapeHtml(arc?.arc || '---')}</h4>
                          ${arc?.description ? `<p class="muted">${escapeHtml(formatValue(arc.description))}</p>` : ''}
                        </article>
                      `).join('')}
                    </div>`
                  : ''}
                ${bi.satisfactionAndCoverage?.thematicCoverage && typeof bi.satisfactionAndCoverage.thematicCoverage === 'object'
                  ? `<div class="field">
                      <div class="field-label muted">Thematic Coverage</div>
                      <div class="pill-row">${Object.entries(bi.satisfactionAndCoverage.thematicCoverage)
                        .map(([key, value]) => `<span class="pill">${escapeHtml(String(key).replace(/[_-]+/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))}: ${escapeHtml(formatValue(value))}</span>`)
                        .join('')}</div>
                    </div>`
                  : ''}
                ${bi.satisfactionAndCoverage?.interviewPatternSummary && typeof bi.satisfactionAndCoverage.interviewPatternSummary === 'object'
                  ? `<div class="field">
                      <div class="field-label muted">Interview Pattern Summary</div>
                      <div class="pill-row">${Object.entries(bi.satisfactionAndCoverage.interviewPatternSummary)
                        .map(([key, value]) => `<span class="pill">${escapeHtml(String(key).replace(/[_-]+/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))}: ${escapeHtml(formatValue(value))}</span>`)
                        .join('')}</div>
                    </div>`
                  : ''}
              </div>`
            : ''}
        </div>
      </section>
    `);
  }
  // Experience Gaps
  if (!isV2 && Array.isArray(data.experienceGaps) && data.experienceGaps.length > 0) {
    section.push(`
      <section class="section-block">
        <h2 class="section-title">Experience Gaps</h2>
        <div class="section-body card-grid">
          ${data.experienceGaps.map((g) => `
            <article class="card">
              <h3 class="card-title">${escapeHtml(g?.label || g?.gapId || '---')}</h3>
              ${g?.priority ? `<div class="pill pill-priority">Priority: ${escapeHtml(g.priority)}</div>` : ''}
              ${g?.currentState
                ? `<div class="field"><div class="field-label muted">Current State</div><div>${escapeHtml(g.currentState)}</div></div>` 
                : ''}
              ${g?.expectedState
                ? `<div class="field"><div class="field-label muted">Expected State</div><div>${escapeHtml(g.expectedState)}</div></div>` 
                : ''}
              ${g?.emotionalConsequence
                ? `<div class="field"><div class="field-label muted">Emotional Consequence</div><div>${escapeHtml(g.emotionalConsequence)}</div></div>` 
                : ''}
            </article>
          `).join('')}
        </div>
      </section>
    `);
  }
  // Action Playbook
  if (!isV2 && data.actionPlaybook && typeof data.actionPlaybook === 'object') {
    const ap = data.actionPlaybook;
    section.push(`
      <section class="section-block">
        <h2 class="section-title">Action Playbook</h2>
        <div class="section-body">
          ${ap.guidingPrinciple
            ? `<div class="field">
                <div class="field-label muted">Guiding Principle</div>
                <div>${escapeHtml(ap.guidingPrinciple)}</div>
              </div>`
            : ''}
          ${Array.isArray(ap.quickWins) && ap.quickWins.length > 0
            ? `<div class="subsection">
                <h3 class="subsection-title">Quick Wins</h3>
                <div class="card-grid">
                  ${ap.quickWins.map((q) => `
                    <article class="card">
                      <h4 class="card-title">${escapeHtml(q?.title || q?.id || '---')}</h4>
                      ${q?.priority
                        ? `<div class="pill pill-priority">${escapeHtml(q.priority)}</div>` 
                        : ''}
                      ${q?.description
                        ? `<p class="muted">${escapeHtml(q.description)}</p>` 
                        : ''}
                      ${Array.isArray(q?.tacticalSteps) && q.tacticalSteps.length > 0
                        ? `<div class="field">
                            <div class="field-label muted">Tactical Steps</div>
                            <div class="pill-row">${q.tacticalSteps
                              .map((step) => `<span class="pill">${escapeHtml(step)}</span>`)
                              .join('')}</div>
                          </div>`
                        : ''}
                      ${q?.targetEmotion
                        ? `<div class="field">
                            <div class="field-label muted">Target Emotion</div>
                            <div>${escapeHtml(q.targetEmotion)}</div>
                          </div>`
                        : ''}
                    </article>
                  `).join('')}
                </div>
              </div>`
            : ''}
          ${Array.isArray(ap.designedExperiments) && ap.designedExperiments.length > 0
            ? `<div class="subsection">
                <h3 class="subsection-title">Designed Experiments</h3>
                <div class="card-grid">
                  ${ap.designedExperiments.map((exp) => {
                    const design = exp?.design || {};
                    return `
                      <article class="card">
                        <h4 class="card-title">${escapeHtml(exp?.name || exp?.experimentId || '---')}</h4>
                        ${exp?.hypothesis ? `<p class="muted">${escapeHtml(exp.hypothesis)}</p>` : ''}
                        ${design && (Array.isArray(design.conditions) || design.measurementWindow || Array.isArray(design.metrics) || Array.isArray(design.feedbackMechanism))
                          ? `<div class="field">
                              <div class="field-label muted">Design</div>
                              ${Array.isArray(design.conditions) && design.conditions.length > 0
                                ? `<div class="field">
                                    <div class="field-label muted">Conditions</div>
                                    <div class="pill-row">${design.conditions
                                      .map((c) => `<span class="pill">${escapeHtml(c)}</span>`)
                                      .join('')}</div>
                                  </div>`
                                : ''}
                              ${design.measurementWindow
                                ? `<div class="field">
                                    <div class="field-label muted">Measurement Window</div>
                                    <div>${escapeHtml(design.measurementWindow)}</div>
                                  </div>`
                                : ''}
                              ${Array.isArray(design.metrics) && design.metrics.length > 0
                                ? `<div class="field">
                                    <div class="field-label muted">Metrics</div>
                                    <div class="pill-row">${design.metrics
                                      .map((m) => `<span class="pill">${escapeHtml(m)}</span>`)
                                      .join('')}</div>
                                  </div>`
                                : ''}
                              ${Array.isArray(design.feedbackMechanism) && design.feedbackMechanism.length > 0
                                ? `<div class="field">
                                    <div class="field-label muted">Feedback Mechanism</div>
                                    <div class="pill-row">${design.feedbackMechanism
                                      .map((f) => `<span class="pill">${escapeHtml(f)}</span>`)
                                      .join('')}</div>
                                  </div>`
                                : ''}
                            </div>`
                          : ''}
                        ${Array.isArray(exp?.successSignals) && exp.successSignals.length > 0
                          ? `<div class="field">
                              <div class="field-label muted">Success Signals</div>
                              <div class="pill-row">${exp.successSignals
                                .map((s) => `<span class="pill">${escapeHtml(s)}</span>`)
                                .join('')}</div>
                            </div>`
                          : ''}
                      </article>
                    `;
                  }).join('')}
                </div>
              </div>`
            : ''}
          ${Array.isArray(ap.longerTermMoves) && ap.longerTermMoves.length > 0
            ? `<div class="subsection">
                <h3 class="subsection-title">Longer-term Moves</h3>
                <div class="card-grid">
                  ${ap.longerTermMoves.map((m) => `
                    <article class="card">
                      <h4 class="card-title">${escapeHtml(m?.title || m?.id || '---')}</h4>
                      ${m?.description ? `<p class="muted">${escapeHtml(m.description)}</p>` : ''}
                      ${Array.isArray(m?.components) && m.components.length > 0
                        ? `<div class="field">
                            <div class="field-label muted">Components</div>
                            <div class="pill-row">${m.components
                              .map((c) => `<span class="pill">${escapeHtml(c)}</span>`)
                              .join('')}</div>
                          </div>`
                        : ''}
                      ${Array.isArray(m?.tactics) && m.tactics.length > 0
                        ? `<div class="field">
                            <div class="field-label muted">Tactics</div>
                            <div class="pill-row">${m.tactics
                              .map((t) => `<span class="pill">${escapeHtml(t)}</span>`)
                              .join('')}</div>
                          </div>`
                        : ''}
                    </article>
                  `).join('')}
                </div>
              </div>`
            : ''}
        </div>
      </section>
    `);
  }
  // KPI Hypotheses
  if (!isV2 && data.kpiHypotheses && typeof data.kpiHypotheses === 'object') {
    const kpi = data.kpiHypotheses;
    section.push(`
      <section class="section-block">
        <h2 class="section-title">KPI Hypotheses</h2>
        <div class="section-body">
          ${Array.isArray(kpi.primaryKPIs) && kpi.primaryKPIs.length > 0
            ? `<div class="subsection">
                <h3 class="subsection-title">Primary KPIs</h3>
                <div class="card-grid">
                  ${kpi.primaryKPIs.map((k) => {
                    const rawDirection = String(k?.expectedDirection || '');
                    const directionLabel = rawDirection.toLowerCase();
                    let directionDisplay = rawDirection;
                    if (directionLabel === 'up' || directionLabel.includes('up') || directionLabel === 'increase' || directionLabel === 'increasing') {
                      directionDisplay = `&uarr; ${rawDirection}`;
                    } else if (directionLabel === 'down' || directionLabel === 'decrease' || directionLabel === 'decreasing' || directionLabel.includes('down')) {
                      directionDisplay = `&darr; ${rawDirection}`;
                    }
                    return `
                      <article class="card">
                        <h4 class="card-title">${escapeHtml(k?.name || '---')}</h4>
                        ${k?.expectedDirection
                          ? `<div class="pill pill-priority">${escapeHtml(directionDisplay)}</div>` 
                          : ''}
                        ${k?.hypothesis
                          ? `<p class="muted">${escapeHtml(k.hypothesis)}</p>` 
                          : ''}
                        ${k?.whyItMatters
                          ? `<div class="field">
                              <div class="field-label muted">Why It Matters</div>
                              <div>${escapeHtml(k.whyItMatters)}</div>
                            </div>`
                          : ''}
                      </article>
                    `;
                  }).join('')}
                </div>
              </div>`
            : ''}
          ${Array.isArray(kpi.secondaryKPIs) && kpi.secondaryKPIs.length > 0
            ? `<div class="subsection">
                <h3 class="subsection-title">Secondary KPIs</h3>
                <div class="card-grid">
                  ${kpi.secondaryKPIs.map((k) => `
                    <article class="card">
                      <h4 class="card-title">${escapeHtml(k?.name || '---')}</h4>
                      ${k?.hypothesis ? `<p class="muted">${escapeHtml(k.hypothesis)}</p>` : ''}
                      ${Array.isArray(k?.measurementIdeas) && k.measurementIdeas.length > 0
                        ? `<div class="field">
                            <div class="field-label muted">Measurement Ideas</div>
                            <div class="pill-row">${k.measurementIdeas
                              .map((mi) => `<span class="pill">${escapeHtml(mi)}</span>`)
                              .join('')}</div>
                          </div>`
                        : ''}
                    </article>
                  `).join('')}
                </div>
              </div>`
            : ''}
        </div>
      </section>
    `);
  }
  // Open Questions
  if (!isV2 && data.openQuestions && typeof data.openQuestions === 'object') {
    const oq = data.openQuestions;
    section.push(`
      <section class="section-block">
        <h2 class="section-title">Open Questions</h2>
        <div class="section-body">
          ${Array.isArray(oq.forFurtherResearch) && oq.forFurtherResearch.length > 0
            ? `<div class="subsection">
                <h3 class="subsection-title">For Further Research</h3>
                <div class="pill-row">${oq.forFurtherResearch
                  .map((q) => `<span class="pill">${escapeHtml(q)}</span>`)
                  .join('')}</div>
              </div>`
            : ''}
          ${Array.isArray(oq.signalsToWatch) && oq.signalsToWatch.length > 0
            ? `<div class="subsection">
                <h3 class="subsection-title">Signals to Watch</h3>
                <div class="pill-row">${oq.signalsToWatch
                  .map((s) => `<span class="pill">${escapeHtml(s)}</span>`)
                  .join('')}</div>
              </div>`
            : ''}
        </div>
      </section>
    `);
  }
  // Comprehensive Report v2 schema
  if (isV2) {
    // The Results
    if (Array.isArray(data.theResults) && data.theResults.length > 0) {
      section.push(`
        <section class="section-block">
          <h2 class="section-title">The Results</h2>
          <div class="section-body">
            <div class="card-grid">
              ${data.theResults.map((result, i) => `
                <article class="card">
                  <h4 class="card-title">
                    <span class="pill">Signal ${result?.signalNumber ?? i + 1}</span>
                    ${escapeHtml(result?.claim || '---')}
                  </h4>
                  ${Array.isArray(result?.qualifiers) && result.qualifiers.length > 0
                    ? `<div class="pill-row">${result.qualifiers
                        .map((q) => `<span class="pill">${escapeHtml(q)}</span>`)
                        .join('')}</div>`
                    : ''}
                </article>
              `).join('')}
            </div>
          </div>
        </section>
      `);
    }

    // Central Finding
    if (data.centralFinding && Array.isArray(data.centralFinding.paragraphs) && data.centralFinding.paragraphs.length > 0) {
      section.push(`
        <section class="section-block">
          <h2 class="section-title">Central Finding</h2>
          <div class="section-body">
            ${data.centralFinding.paragraphs.map((paragraph) => `<p>${escapeHtml(paragraph)}</p>`).join('')}
          </div>
        </section>
      `);
    }

    // What Participants Told Us
    if (Array.isArray(data.whatParticipantsToldUs) && data.whatParticipantsToldUs.length > 0) {
      section.push(`
        <section class="section-block">
          <h2 class="section-title">What Participants Told Us</h2>
          <div class="section-body">
            <div class="card-grid">
              ${data.whatParticipantsToldUs.map((finding) => `
                <article class="card">
                  <h4 class="card-title">${escapeHtml(finding?.findingTitle || '---')}</h4>
                  ${finding?.narrative ? `<p class="muted">${escapeHtml(finding.narrative)}</p>` : ''}
                  ${Array.isArray(finding?.quotes) && finding.quotes.length > 0
                    ? `<div class="field">
                        <div class="field-label muted">Quotes</div>
                        <div class="pill-row">${finding.quotes
                          .map((q) => `<span class="quote">“${escapeHtml(q?.text || '')}”${q?.sessionId ? ` <em>(Interview ${escapeHtml(q.sessionId)}${q?.qualifier ? ` · ${escapeHtml(q.qualifier)}` : ''})</em>` : ''}</span>`)
                          .join('')}</div>
                      </div>`
                    : ''}
                  ${finding?.doingDifferently
                    ? `<div class="field"><div class="field-label muted">Doing Differently</div><div>${escapeHtml(finding.doingDifferently)}</div></div>`
                    : ''}
                  ${finding?.wouldStrengthen
                    ? `<div class="field"><div class="field-label muted">Would Strengthen</div><div>${escapeHtml(finding.wouldStrengthen)}</div></div>`
                    : ''}
                </article>
              `).join('')}
            </div>
          </div>
        </section>
      `);
    }

    // Answers to the Brief
    if (Array.isArray(data.answersToTheBrief) && data.answersToTheBrief.length > 0) {
      section.push(`
        <section class="section-block">
          <h2 class="section-title">Answers to the Brief</h2>
          <div class="section-body">
            <div class="card-grid">
              ${data.answersToTheBrief.map((row) => `
                <article class="card">
                  <h4 class="card-title">
                    ${escapeHtml(row?.objectiveId || '---')}
                    ${row?.status ? `<span class="pill">${escapeHtml(OBJECTIVE_STATUS_LABELS[row.status] || row.status)}</span>` : ''}
                  </h4>
                  ${row?.objectiveText ? `<p class="muted">${escapeHtml(row.objectiveText)}</p>` : ''}
                  ${row?.summary ? `<p>${escapeHtml(row.summary)}</p>` : ''}
                </article>
              `).join('')}
            </div>
          </div>
        </section>
      `);
    }

    // The Opportunity
    if (Array.isArray(data.theOpportunity) && data.theOpportunity.length > 0) {
      section.push(`
        <section class="section-block">
          <h2 class="section-title">The Opportunity</h2>
          <div class="section-body">
            <div class="card-grid">
              ${data.theOpportunity.map((item, i) => `
                <article class="card">
                  <h4 class="card-title"><span class="pill">${i + 1}</span> ${escapeHtml(item?.question || '---')}</h4>
                </article>
              `).join('')}
            </div>
          </div>
        </section>
      `);
    }

    // What to Learn Next
    if (Array.isArray(data.whatToLearnNext) && data.whatToLearnNext.length > 0) {
      section.push(`
        <section class="section-block">
          <h2 class="section-title">What to Learn Next</h2>
          <div class="section-body">
            <div class="card-grid">
              ${data.whatToLearnNext.map((item, i) => `
                <article class="card">
                  <h4 class="card-title"><span class="pill">${i + 1}</span> ${escapeHtml(item?.question || '---')}</h4>
                </article>
              `).join('')}
            </div>
          </div>
        </section>
      `);
    }

    // Evidence & Limitations
    if (data.evidenceAndLimitations && typeof data.evidenceAndLimitations === 'object') {
      const ev = data.evidenceAndLimitations;
      section.push(`
        <section class="section-block">
          <h2 class="section-title">Evidence &amp; Limitations</h2>
          <div class="section-body">
            <div class="card-grid">
              ${ev.participation ? `<div class="field"><div class="field-label muted">Participation</div><div>${escapeHtml(ev.participation)}</div></div>` : ''}
              ${ev.audience ? `<div class="field"><div class="field-label muted">Audience</div><div>${escapeHtml(ev.audience)}</div></div>` : ''}
              ${ev.recruitment ? `<div class="field"><div class="field-label muted">Recruitment</div><div>${escapeHtml(ev.recruitment)}</div></div>` : ''}
              ${ev.interviewDepth ? `<div class="field"><div class="field-label muted">Depth</div><div>${escapeHtml(ev.interviewDepth)}</div></div>` : ''}
            </div>
            ${Array.isArray(ev.cannotEstablish) && ev.cannotEstablish.length > 0
              ? `<div class="subsection">
                  <h3 class="subsection-title">Cannot Establish</h3>
                  <ul>
                    ${ev.cannotEstablish.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}
                  </ul>
                </div>`
              : ''}
            ${ev.augustComparison
              ? `<div class="subsection">
                  <h3 class="subsection-title">August Comparison</h3>
                  <p>${escapeHtml(ev.augustComparison)}</p>
                </div>`
              : ''}
          </div>
        </section>
      `);
    }
  }
  const printWindow = window.open('', '_blank');
  if (!printWindow) return;
  const doc = printWindow.document;
  doc.open();
  doc.write(`<!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${safeName} &ndash; Comprehensive Report</title>
        <style>
          * { box-sizing: border-box; }
          body { font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 24px; color: #111827; line-height: 1.5; }
          h1 { font-size: 24px; margin: 0 0 4px; }
          h2 { font-size: 20px; margin: 0 0 8px; }
          h3 { font-size: 16px; margin: 16px 0 4px; }
          h4 { font-size: 14px; margin: 0 0 4px; }
          .meta { font-size: 12px; color: #6b7280; margin-bottom: 16px; }
          .section-block { margin-bottom: 24px; page-break-inside: avoid; }
          .section-title { border-bottom: 1px solid #e5e7eb; padding-bottom: 4px; margin-bottom: 12px; font-weight: 600; }
          .section-body { font-size: 13px; }
          .lede { font-weight: 600; margin-bottom: 8px; }
          .subsection { margin-top: 12px; }
          .subsection-title { font-weight: 600; font-size: 14px; margin-bottom: 6px; }
          .card-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px; }
          .card { border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px 12px; background: #ffffff; break-inside: avoid; }
          .card-title { font-weight: 600; margin-bottom: 4px; }
          .muted { color: #6b7280; }
          .field { margin-top: 6px; }
          .field-label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 2px; }
          .pill-row { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 4px; }
          .pill { display: inline-block; padding: 2px 8px; border-radius: 999px; background: #eff6ff; color: #1d4ed8; font-size: 11px; }
          .pill-priority { background: #fef3c7; color: #92400e; }
          .quote { display: inline-block; padding: 4px 8px; border-left: 2px solid #d1d5db; font-size: 11px; background: #f9fafb; }
          @page { margin: 20mm; }
        </style>
      </head>
      <body>
        <h1>Comprehensive Report</h1>
        <div class="meta">
          Canvass: ${safeName}<br/>
          ${header?.organisation ? `Organisation: ${escapeHtml(header.organisation)}<br/>` : ''}
          ${header?.date ? `Date: ${escapeHtml(header.date)}<br/>` : ''}
          Generated at: ${new Date().toLocaleString()}<br/>
          ${meta?.generatedBy ? `Generated by: ${escapeHtml(meta.generatedBy)}` : ''}
        </div>
        ${section.join('\n')}
      </body>
    </html>`);
  doc.close();
  printWindow.focus();
  printWindow.print();
};

// Export all functions
export {
  getAccessToken,
  makeAuthenticatedRequest,
};
